import { SEO } from "../components/SEO";

export function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Valkyrie Robotics. Learn how we collect, use, and protect your personal information when participating in our VEX Robotics programs."
        canonical="/privacy"
        keywords={[
          "privacy policy",
          "Valkyrie Robotics privacy",
          "data protection",
          "personal information",
          "student privacy",
          "robotics program privacy",
        ]}
      />
      <div className="bg-neutral-900 min-h-screen">
        <div className="mx-auto max-w-4xl px-4 py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Privacy Policy
          </h1>

          <div className="prose prose-invert prose-neutral prose-lg max-w-none [&_a]:text-brand-400 [&_a]:hover:text-brand-300 [&_a]:underline [&_a]:transition-colors [&_a]:duration-300">
            <div className="bg-neutral-800/30 rounded-2xl p-8 md:p-12 border border-neutral-700">
              <p className="text-gray-300 mb-6">
                <strong>Last updated:</strong>{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-300 mb-6">
                Valkyrie Robotics, LLC ("Company," "we," "us," or "our") is
                committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your
                information when you visit our website or participate in our
                robotics programs.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-medium text-white mb-3">
                2.1 Personal Information
              </h3>
              <p className="text-gray-300 mb-4">
                We may collect personal information that you voluntarily provide
                to us when you:
              </p>
              <ul className="text-gray-300 mb-6 list-disc list-inside space-y-2">
                <li>Register for our robotics programs</li>
                <li>Fill out contact forms</li>
                <li>Subscribe to our newsletter</li>
                <li>Participate in our programs</li>
                <li>Contact us for support</li>
              </ul>
              <p className="text-gray-300 mb-6">
                We collect photographs, videos, and security camera recordings
                during program participation for safety purposes. Promotional
                use of such materials requires prior written parental/guardian
                consent.
              </p>

              <p className="text-gray-300 mb-6">
                This information may include names, email addresses, phone
                numbers, addresses, student ages, emergency contact information,
                and other details necessary for program participation.
              </p>

              <h3 className="text-xl font-medium text-white mb-3">
                2.2 Automatically Collected Information
              </h3>
              <p className="text-gray-300 mb-6">
                When you visit our website, and only our website, we
                automatically collect certain information about your device,
                including information about your web browser, IP address, time
                zone, and some of the cookies that are installed on your device.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-300 mb-4">
                We use the information we collect to:
              </p>
              <ul className="text-gray-300 mb-6 list-disc list-inside space-y-2">
                <li>Provide and maintain our robotics programs</li>
                <li>Process registrations and payments</li>
                <li>Communicate with you about programs and updates</li>
                <li>Ensure student safety during activities</li>
                <li>Improve our services and website</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h2 className="text-2xl font-semibold text-white mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-gray-300 mb-6">
                We do not sell, trade, or otherwise transfer your personal
                information to third parties without your consent, except in the
                following circumstances:
              </p>
              <ul className="text-gray-300 mb-6 list-disc list-inside space-y-2">
                <li>With your explicit consent</li>
                <li>To comply with legal requirements</li>
                <li>To protect our rights and safety</li>
                <li>
                  With service providers who assist in our operations (under
                  strict confidentiality agreements)
                </li>
              </ul>
              <p className="text-gray-300 mb-6">
                If you make a payment via Zelle for Business, your name and
                payment details may be processed by Zelle and your financial
                institution in accordance with their privacy policies.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                5. Data Security
              </h2>
              <p className="text-gray-300 mb-6">
                We implement appropriate security measures to protect your
                personal information against unauthorized access, alteration,
                disclosure, or destruction. However, no method of transmission
                over the internet or electronic storage is 100% secure, and we
                cannot guarantee absolute security. Physical forms are stored in
                locked cabinets with restricted access, and surveillance
                recordings are stored securely with limited access.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                6. Children's Privacy
              </h2>
              <p className="text-gray-300 mb-6">
                Our programs are designed for students, and we collect
                information about children under 13 with parental consent. We
                are committed to protecting children's privacy and comply with
                applicable children's privacy laws and regulations. We comply
                with the Children's Online Privacy Protection Act (COPPA) and
                only collect personal information from children under 13 with
                verified parental consent.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-300 mb-6">
                We use cookies and similar tracking technologies to track
                activity on our website and hold certain information. You can
                instruct your browser to refuse all cookies or to indicate when
                a cookie is being sent. We do not use cookies or tracking
                technologies to collect personal information from children under
                13 for advertising purposes.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                8. Third-Party Services
              </h2>
              <p className="text-gray-300 mb-6">
                Our website may contain links to third-party websites or
                services. We are not responsible for the privacy practices of
                these third parties. We encourage you to read their privacy
                policies.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                9. Your Rights and Choices
              </h2>
              <p className="text-gray-300 mb-4">You have the right to:</p>
              <ul className="text-gray-300 mb-6 list-disc list-inside space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for data processing</li>
              </ul>

              <h2 className="text-2xl font-semibold text-white mb-4">
                10. Data Retention
              </h2>
              <p className="text-gray-300 mb-6">
                We retain your personal information for as long as necessary to
                fulfill the purposes outlined in this Privacy Policy, unless a
                longer retention period is required or permitted by law. We
                retain personal information about children for up to 3 years
                after program participation ends, and security camera footage is
                retained for no longer than 90 days unless required for an
                incident investigation.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-300 mb-6">
                We may update this Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last updated" date.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                12. Contact Us
              </h2>
              <p className="text-gray-300 mb-6">
                If you have any questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="bg-neutral-700/50 rounded-lg p-4 mb-6">
                <p className="text-gray-300 mb-2">
                  <strong>Valkyrie Robotics, LLC</strong>
                  <br />
                  Email:{" "}
                  <a href="mailto:contact@valkyrie-robotics.com">
                    contact@valkyrie-robotics.com
                  </a>
                  <br />
                  Phone: <a href="tel:+1-470-239-0787">+1-470-239-0787</a>
                  <br />
                  Address: Forsyth County, GA
                </p>
              </div>

              <div className="border-t border-neutral-600 pt-6 mt-8">
                <p className="text-sm text-gray-400">
                  This Privacy Policy is effective as of the date listed above
                  and will remain in effect except with respect to any changes
                  in its provisions in the future, which will be in effect
                  immediately after being posted on this page.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
